import { useEffect, useLayoutEffect, useRef } from "react";
import { cn } from "@/lib/utils";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

export function Input({ label, error, className, ...props }: InputProps) {
  return (
    <div className="flex flex-col gap-1">
      {label && <label className="text-xs font-medium text-gray-400">{label}</label>}
      <input
        className={cn(
          "w-full px-3 py-2 rounded-xl text-sm text-white placeholder-gray-600 border outline-none transition-all",
          "focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/20",
          className,
        )}
        style={{
          backgroundColor: "#111318",
          borderColor: "rgba(229,231,235,0.12)",
        }}
        {...props}
      />
      {error && <span className="text-xs text-red-400">{error}</span>}
    </div>
  );
}

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
}

/**
 * Textarea that auto-grows to fit its content (up to a sensible max height)
 * so pasted paragraphs aren't trapped behind a tiny scrollbar. The user can
 * still drag the bottom-right handle vertically to override.
 */
export function Textarea({ label, error, className, ...props }: TextareaProps) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const adjust = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    // Cap at 480px so very long pastes don't push the page layout around;
    // beyond that the textarea grows internal scroll.
    const maxH = 480;
    const next = Math.min(el.scrollHeight + 2, maxH);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > maxH ? "auto" : "hidden";
  };

  // Initial layout pass + whenever the controlled value changes.
  useLayoutEffect(() => {
    adjust();
  }, [props.value]);

  // Catch resize so wrap recomputes when the page width changes.
  useEffect(() => {
    const onResize = () => adjust();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <div className="flex flex-col gap-1">
      {label && <label className="text-xs font-medium text-gray-400">{label}</label>}
      <textarea
        ref={ref}
        className={cn(
          "w-full px-3 py-2 rounded-xl text-sm text-white placeholder-gray-600 border outline-none transition-all resize-y",
          "focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/20",
          "min-h-[80px]",
          className,
        )}
        style={{
          backgroundColor: "#111318",
          borderColor: "rgba(229,231,235,0.12)",
        }}
        {...props}
        onInput={(e) => {
          adjust();
          props.onInput?.(e);
        }}
      />
      {error && <span className="text-xs text-red-400">{error}</span>}
    </div>
  );
}

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  options: { value: string; label: string }[];
}

export function Select({ label, error, options, className, ...props }: SelectProps) {
  return (
    <div className="flex flex-col gap-1">
      {label && <label className="text-xs font-medium text-gray-400">{label}</label>}
      <select
        className={cn(
          "w-full px-3 py-2 rounded-xl text-sm text-white border outline-none transition-all appearance-none cursor-pointer",
          "focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/20",
          className,
        )}
        style={{
          backgroundColor: "#111318",
          borderColor: "rgba(229,231,235,0.12)",
        }}
        {...props}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} style={{ backgroundColor: "#111318" }}>
            {o.label}
          </option>
        ))}
      </select>
      {error && <span className="text-xs text-red-400">{error}</span>}
    </div>
  );
}
